import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { FundRequest } from '../../types';
import apiService from '../../services/api';
import { 
  Card, 
  CardHeader, 
  CardBody, 
  Button, 
  Badge,
  Input,
  TextArea,
  FormGroup,
  Label
} from '../../styles/StyledComponents';
import IconWrapper from '../common/IconWrapper';
import { 
  FaArrowLeft, 
  FaCheckCircle, 
  FaTimes, 
  FaEye, 
  FaDownload,
  FaFilePdf,
  FaHistory,
  FaComment
} from 'react-icons/fa';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  h1 {
    color: var(--secondary-color);
    font-size: 1.8rem;
    margin: 0;
    
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1.3rem;
    }
  }
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--light-gray);
  color: var(--secondary-color);
  
  &:hover {
    background: var(--medium-gray);
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.9rem;
  }
`;

const RequestHeader = styled.div`
  margin-bottom: 2rem;
  
  .request-id {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--secondary-color);
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.3rem;
    }
  }
  
  .request-project {
    font-size: 1.1rem;
    color: var(--dark-gray);
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
  
  .request-status {
    display: inline-block;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    font-weight: 600;
    font-size: 0.9rem;
    
    @media (max-width: 768px) {
      font-size: 0.8rem;
      padding: 0.4rem 0.8rem;
    }
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const InfoCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  .info-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
    
    .info-icon {
      font-size: 1.5rem;
      color: var(--primary-color);
      
      @media (max-width: 768px) {
        font-size: 1.3rem;
      }
    }
    
    .info-title {
      font-weight: 600;
      color: var(--secondary-color);
      font-size: 1.1rem;
      
      @media (max-width: 768px) {
        font-size: 1rem;
      }
    }
  }
  
  .info-content {
    .info-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 0.75rem 0;
      border-bottom: 1px solid var(--light-gray);
      
      &:last-child {
        border-bottom: none;
      }
      
      .label {
        color: var(--dark-gray);
        font-weight: 500;
        font-size: 0.9rem;
        
        @media (max-width: 768px) {
          font-size: 0.85rem;
        }
      }
      
      .value {
        font-weight: 600;
        color: var(--secondary-color);
        font-size: 0.95rem;
        
        @media (max-width: 768px) {
          font-size: 0.9rem;
        }
      }
    }
  }
`;

const ItemsCard = styled(Card)`
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  .items-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    gap: 1rem;
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }
  
  .item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    background: var(--light-gray);
    border-radius: 8px;
    margin-bottom: 0.75rem;
    
    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
    }
    
    .item-info {
      flex: 1;
      
      .item-description {
        font-weight: 600;
        color: var(--secondary-color);
        margin-bottom: 0.25rem;
        font-size: 1rem;
        
        @media (max-width: 768px) {
          font-size: 0.9rem;
        }
      }
      
      .item-details {
        font-size: 0.85rem;
        color: var(--dark-gray);
        
        @media (max-width: 768px) {
          font-size: 0.8rem;
        }
      }
    }
    
    .item-amount {
      font-weight: 700;
      color: var(--primary-color);
      font-size: 1.1rem;
      
      @media (max-width: 768px) {
        font-size: 1rem;
      }
    }
  }
`;

const ApprovalChainCard = styled(Card)`
  margin-bottom: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  
  .approval-step {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 0;
    border-bottom: 1px solid var(--light-gray);
    
    &:last-child {
      border-bottom: none;
    }
    
    @media (max-width: 768px) {
      gap: 0.75rem;
    }
    
    .step-icon {
      width: 40px;
      height: 40px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-weight: 600;
      flex-shrink: 0;
      
      @media (max-width: 768px) {
        width: 35px;
        height: 35px;
      }
    }
    
    .step-info {
      flex: 1;
      
      .step-role {
        font-weight: 600;
        color: var(--secondary-color);
        margin-bottom: 0.25rem;
        font-size: 1rem;
        
        @media (max-width: 768px) {
          font-size: 0.9rem;
        }
      }
      
      .step-status {
        font-size: 0.85rem;
        color: var(--dark-gray);
        
        @media (max-width: 768px) {
          font-size: 0.8rem;
        }
      }
    }
    
    .step-status-badge {
      padding: 0.25rem 0.5rem;
      border-radius: 12px;
      font-size: 0.75rem;
      font-weight: 600;
      
      @media (max-width: 768px) {
        font-size: 0.7rem;
      }
    }
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
  }
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--dark-gray);
  
  .empty-icon {
    font-size: 4rem;
    color: var(--light-gray);
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      font-size: 3rem;
    }
  }
  
  .empty-title {
    font-size: 1.3rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }
  
  .empty-description {
    font-size: 1rem;
    line-height: 1.6;
    max-width: 400px;
    margin: 0 auto;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
      max-width: 300px;
    }
  }
`;

const SupervisorFundRequestDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [request, setRequest] = useState<FundRequest | null>(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFundRequest = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        console.log('Fetching fund request with ID:', id);
        
        const response = await apiService.getFundRequest(id);
        console.log('Fund request response:', response);
        
        if (response.success && response.data) {
          setRequest(response.data);
        } else {
          console.error('Fund request not found or no access');
          setRequest(null);
        }
      } catch (error) {
        console.error('Error fetching fund request:', error);
        setRequest(null);
      } finally {
        setLoading(false);
      }
    };

    fetchFundRequest();
  }, [id]);

  const formatCurrency = (amount: number, currency: string = 'TZS') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusVariant = (status: string): 'success' | 'danger' | 'warning' | 'info' | 'primary' => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Rejected': return 'danger';
      case 'Under Review': return 'warning';
      case 'Paid': return 'info';
      case 'Draft': return 'primary';
      default: return 'warning';
    }
  };

  const getStepIconColor = (status: string) => {
    switch (status) {
      case 'Approved': return '#28a745';
      case 'Rejected': return '#dc3545';
      case 'Pending': return '#6c757d';
      case 'PAID': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  const getDisplayStatus = (step: any) => {
    // If Accountant approved, show "PAID" instead of "Approved"
    if (step.role === 'Accountant' && step.status === 'Approved') {
      return 'PAID';
    }
    return step.status;
  };

  const canApproveOrReject = (): boolean => {
    if (!request || !user) return false;
    const pendingStep = request.approvalChain.find(step => step.status === 'Pending');
    return pendingStep?.role === 'Supervisor';
  };

  const handleApprove = async () => {
    if (!request) return;
    
    try {
      const approvalData = {
        action: 'approve',
        comments: `Approved by ${user?.role}`,
        approvedBy: user?.id,
        approvedAt: new Date().toISOString()
      };

      const response = await apiService.approveFundRequest(request._id || request.id || '', approvalData);
      
      if (response.success) {
        // Update the local state with the approved request
        setRequest(response.data);
        alert('Fund request approved successfully!');
      } else {
        alert('Failed to approve fund request: ' + response.message);
      }
    } catch (error: any) {
      console.error('Error approving fund request:', error);
      alert('Error approving fund request: ' + error.message);
    }
  };

  const handleReject = async () => {
    if (!request) return;
    
    try {
      const rejectionData = {
        action: 'reject',
        comments: `Rejected by ${user?.role}`,
        rejectedBy: user?.id,
        rejectedAt: new Date().toISOString()
      };

      const response = await apiService.approveFundRequest(request._id || request.id || '', rejectionData);
      
      if (response.success) {
        // Update the local state with the rejected request
        setRequest(response.data);
        alert('Fund request rejected successfully!');
      } else {
        alert('Failed to reject fund request: ' + response.message);
      }
    } catch (error: any) {
      console.error('Error rejecting fund request:', error);
      alert('Error rejecting fund request: ' + error.message);
    }
  };

  const handleAddComment = () => {
    if (!comment.trim() || !request) return;
    
    // In a real app, this would make an API call
    console.log('Adding comment:', comment);
    setComment('');
  };

  if (loading) {
    return (
      <PageContainer>
        <PageHeader>
          <h1>Fund Request Details</h1>
          <BackButton as={Link} to="/supervisor/funds">
            <IconWrapper icon={FaArrowLeft} />
            Back to Requests
          </BackButton>
        </PageHeader>
        
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          fontSize: '1.1rem',
          color: '#6c757d'
        }}>
          Loading fund request details...
        </div>
      </PageContainer>
    );
  }

  if (!request) {
    return (
      <PageContainer>
        <PageHeader>
          <h1>Fund Request Details</h1>
          <BackButton as={Link} to="/supervisor/funds">
            <IconWrapper icon={FaArrowLeft} />
            Back to Requests
          </BackButton>
        </PageHeader>
        
        <NoDataMessage>
          <div className="empty-icon">🔍</div>
          <div className="empty-title">Request Not Found</div>
          <div className="empty-description">
            The requested fund request could not be found or you don't have permission to view it.
          </div>
        </NoDataMessage>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <h1>Fund Request Details</h1>
        <BackButton as={Link} to="/supervisor/funds">
          <IconWrapper icon={FaArrowLeft} />
          Back to Requests
        </BackButton>
      </PageHeader>

      <RequestHeader>
        <div className="request-id">{request.id}</div>
        <div className="request-project">{request.projectName}</div>
        <Badge variant={getStatusVariant(request.status)} className="request-status">
          {request.status}
        </Badge>
      </RequestHeader>

      <InfoGrid>
        <InfoCard>
          <div className="info-header">
            <IconWrapper icon={FaEye} className="info-icon" />
            <div className="info-title">Request Information</div>
          </div>
          <div className="info-content">
            <div className="info-item">
              <span className="label">Requester</span>
              <span className="value">{request.requester}</span>
            </div>
            <div className="info-item">
              <span className="label">Timeline</span>
              <span className="value">{request.timeline}</span>
            </div>
            <div className="info-item">
              <span className="label">Created</span>
              <span className="value">{formatDate(request.createdAt)}</span>
            </div>
            <div className="info-item">
              <span className="label">Last Updated</span>
              <span className="value">{formatDate(request.updatedAt)}</span>
            </div>
          </div>
        </InfoCard>

        <InfoCard>
          <div className="info-header">
            <IconWrapper icon={FaHistory} className="info-icon" />
            <div className="info-title">Financial Information</div>
          </div>
          <div className="info-content">
            <div className="info-item">
              <span className="label">Total Amount</span>
              <span className="value">{formatCurrency(request.amount, request.currency)}</span>
            </div>
            <div className="info-item">
              <span className="label">Currency</span>
              <span className="value">{request.currency}</span>
            </div>
            <div className="info-item">
              <span className="label">Items Count</span>
              <span className="value">{request.items.length}</span>
            </div>
            {request.justification && (
              <div className="info-item">
                <span className="label">Justification</span>
                <span className="value">{request.justification}</span>
              </div>
            )}
          </div>
        </InfoCard>
      </InfoGrid>

      <ItemsCard>
        <div className="items-header">
          <h3>Requested Items</h3>
          <div style={{ fontSize: '0.9rem', color: 'var(--dark-gray)' }}>
            {request.items.length} items
          </div>
        </div>
        
        {request.items.map((item, index) => (
          <div key={index} className="item">
            <div className="item-info">
              <div className="item-description">{item.description}</div>
              <div className="item-details">
                Qty: {item.quantity} × {formatCurrency(item.unitPrice, request.currency)} | 
                Category: {item.category}
                {item.specifications && ` | ${item.specifications}`}
              </div>
            </div>
            <div className="item-amount">
              {formatCurrency(item.totalPrice, request.currency)}
            </div>
          </div>
        ))}
      </ItemsCard>

      <ApprovalChainCard>
        <div className="info-header">
          <IconWrapper icon={FaHistory} className="info-icon" />
          <div className="info-title">Approval Chain</div>
        </div>
        
        {request.approvalChain.map((step, index) => (
          <div key={index} className="approval-step">
            <div 
              className="step-icon" 
              style={{ backgroundColor: getStepIconColor(getDisplayStatus(step)) }}
            >
              {getDisplayStatus(step) === 'Approved' ? '✓' : 
               getDisplayStatus(step) === 'Rejected' ? '✗' : 
               getDisplayStatus(step) === 'Pending' ? '⏳' : 
               getDisplayStatus(step) === 'PAID' ? '💰' : '○'}
            </div>
            <div className="step-info">
              <div className="step-role">{step.role}</div>
              <div className="step-status">
                {getDisplayStatus(step)} {step.timestamp && `- ${formatDate(step.timestamp)}`}
                {step.comments && ` - ${step.comments}`}
              </div>
            </div>
            <Badge variant={getStatusVariant(getDisplayStatus(step))} className="step-status-badge">
              {getDisplayStatus(step)}
            </Badge>
          </div>
        ))}
      </ApprovalChainCard>

      {canApproveOrReject() && (
        <Card>
          <CardHeader>
            <h3>Take Action</h3>
          </CardHeader>
          <CardBody>
            <div style={{ marginBottom: '1rem' }}>
              <Label>Add Comment (Optional)</Label>
              <TextArea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add a comment about your decision..."
                rows={3}
              />
            </div>
            <ActionButtons>
              <Button 
                onClick={handleApprove}
                style={{ backgroundColor: 'var(--success-color)' }}
              >
                <IconWrapper icon={FaCheckCircle} />
                Approve Request
              </Button>
              <Button 
                onClick={handleReject}
                style={{ backgroundColor: 'var(--danger-color)' }}
              >
                <IconWrapper icon={FaTimes} />
                Reject Request
              </Button>
            </ActionButtons>
          </CardBody>
        </Card>
      )}

      <ActionButtons>
        <Button as={Link} to={`/supervisor/funds/${request.id}/edit`}>
          <IconWrapper icon={FaEye} />
          Edit Request
        </Button>
        <Button>
          <IconWrapper icon={FaDownload} />
          Export PDF
        </Button>
        <Button>
          <IconWrapper icon={FaFilePdf} />
          Print
        </Button>
      </ActionButtons>
    </PageContainer>
  );
};

export default SupervisorFundRequestDetail; 